import {
	type CreatePullRequestOption,
	type CreatePullReviewOptions,
	type PullRequest,
	giteaApi as createApi,
} from "gitea-js";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import update from "lodash-es/update";
import { type ActorRefFromLogic } from "xstate";
import YAML from "yaml";
import { check, z } from "zod/v4";

import { DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { LOCAL_BASE_URL_DEFAULT, currentBaseUrl } from "~/auth/interop/common";
import {
	type DocChangePermRequest,
	type DocPermission,
	type Invitation,
	InviteResultSchema,
	type PermRelation,
	type PermissionInfo,
	type SearchUserResult,
} from "~/state/types";
import { LOCAL_WORKSPACE_NAME } from "~/strings";

import { type AuthenticationMachine, getAccessToken } from "../machines/authentication";
import { type Repo, type Workspace } from "./types";

const logger = new Logger("docsapi");

export type CheckDocPermArgs = {
	token: string | undefined;
	workspaceName: string;
	documentId: string;
};

export type BaseUpdatePermArgs = {
	token: string;
	workspaceName: string;
	documentId: string;
	type: "grant" | "revoke";
	relation: PermRelation;
};

export class DocsApiError extends Error {
	constructor(message: string, cause?: Response) {
		super(message);
		this.name = "DocsApiError";
		this.cause = cause;
	}
}

export const getDocsApiUrl = (): string => {
	// set NEXT_PUBLIC_GITEA_API_URL env to  "localhost:8088" if you want to use local backend.
	const giteaApiUrl = process.env["NEXT_PUBLIC_GITEA_API_URL"];

	if (typeof giteaApiUrl === "string" && !isEmpty(giteaApiUrl)) {
		if (giteaApiUrl.startsWith("http")) {
			return giteaApiUrl;
		}
		return `https://${giteaApiUrl}`;
	}

	if (process.env.APP_ENV === "production") {
		return "https://git.moment.dev";
	}

	return "https://git-staging.moment.dev";
};

export const getDocsApiUrlWithoutProtocol = (): string => {
	const apiUrl = getDocsApiUrl();
	return apiUrl.replace(/^https?:\/\//, "");
};

// return the docs-staging.moment.dev or docs.moment.dev
export const getDocsWebsite = (): string => {
	if (process.env.VERCEL_URL) {
		return `https://${process.env.VERCEL_URL}`;
	}

	// On local, force to local urls (instead of passed env)
	if (currentBaseUrl === LOCAL_BASE_URL_DEFAULT) {
		return LOCAL_BASE_URL_DEFAULT;
	}

	const apiUrl = getDocsApiUrl();
	return apiUrl.replace(/^https:\/\/git/, "https://docs");
};

// we should move away from using this,
// but this is useful for prototyping
const giteaClient = (token?: string) =>
	createApi(getDocsApiUrl(), {
		customFetch: docsApiFetch(),
	});

function docsApiFetch(token?: string, autothrow: boolean = true) {
	return async (
		input: string | URL | globalThis.Request,
		init?: RequestInit
	): Promise<Response> => {
		const headers = await buildRawRequestHeaders(init?.headers);

		const response = await fetch(input, {
			...init,
			headers,
		});

		if (!response.ok && autothrow) {
			logger.error(`Failed to fetch ${input}`, { response, input, init });
			throw new DocsApiError(response.statusText, response);
		}

		return response;
	};
}

// this is used to fetch from docsapi endpoints, with prefix /internal/v1
// only use this for JSON responses
export const createDocsApiFetch = (token?: string | undefined, autothrow: boolean = true) => {
	return (path: string, init: RequestInit) => {
		const url = `${getDocsApiUrl()}/internal/v1${path}`;
		const fetch = docsApiFetch(token, autothrow);
		return fetch(url, init).then((response) => {
			return response.json();
		});
	};
};

const buildRawRequestHeaders = async (init?: HeadersInit, token?: string) => {
	if (!token) {
		token = await getAccessToken();
	}

	const headers = new Headers(init);
	headers.set("Accept", "application/json");
	headers.set("Content-Type", "application/json");

	if (token) {
		headers.set("Authorization", `Bearer ${token}`);
	}

	return headers;
};

const callUpdatePermissions = async ({
	url,
	token,
	type,
	body,
}: {
	url: string;
	type: "grant" | "revoke";
	token: string;
	body: DocChangePermRequest;
}): Promise<void> => {
	const method = type === "grant" ? "POST" : "DELETE";

	const docsapiClient = createDocsApiFetch(token, true);
	const result = await docsapiClient(url, {
		method,
		body: JSON.stringify(body),
	});
	if (result !== "OK") {
		throw new DocsApiError("Failed to change permission", result);
	}
};

const lookupMetadata = async (
	workspaceName: string,
	documentId: string,
	authenticationActorRef?: ActorRefFromLogic<AuthenticationMachine>
) => {
	const accessToken = await getAccessToken(authenticationActorRef);

	const headers = new Headers();
	headers.set("Accept", "application/json");

	if (accessToken) {
		headers.set("Authorization", `Bearer ${accessToken}`);
	}

	const response = await fetch(
		`${getDocsApiUrl()}/api/v1/repos/${workspaceName}/${documentId}/raw/moment.yml?ref=main`,
		{
			headers: await buildRawRequestHeaders(),
		}
	);

	// since we use the admin endpoint,
	// when we call this, this returns a 500.
	if (!response.ok) {
		// This is lame, but gitea sends a 500 when the document metadata is not found
		// using the admin token and the Sudo header.
		// this is lame. We can change this to a proxy call in docsapi if this is super annoying.
		if (response.status === 404 || response.status === 500) {
			throw new DocsApiError(
				`Document metadata not found for ${workspaceName}/${documentId}`,
				response
			);
		}

		console.error("Failed to fetch document metadata from API", {
			workspaceName,
			documentId,
			response,
		});
		throw new Error("Failed to fetch document metadata from API");
	}
	return response;
};

export const docsApi = {
	document: {
		createRemote: async (
			token: string,
			workspaceName: string,
			documentID: string
		): Promise<Repo> => {
			const docsapiClient = createDocsApiFetch(token);
			const repo = await docsapiClient(`/repos/${encodeURIComponent(workspaceName)}`, {
				method: "POST",
				body: JSON.stringify({ documentID }),
			});

			return repo;
		},
		deleteRemote: async (workspaceName: string, documentId: string) => {
			if (!workspaceName || workspaceName === LOCAL_WORKSPACE_NAME) {
				throw new Error("Cannot delete document from local workspace");
			}
			// delete is a bit special, where we do not return any content, and just a 204.
			// thus we are going to write a custom fetch rather than using the docsApiFetch.

			const headers = await buildRawRequestHeaders();

			try {
				const response = await fetch(
					`${getDocsApiUrl()}/internal/v1/repos/${encodeURIComponent(workspaceName)}/${encodeURIComponent(documentId)}`,
					{
						headers,
						method: "DELETE",
					}
				);
				if (!response.ok) {
					if (response.status === 404) {
						return;
					}
					logger.error("Failed to delete remote document", {
						workspaceName,
						documentId,
						response,
					});
					throw new DocsApiError("Failed to delete remote document", response);
				}
			} catch (e) {
				console.error("Failed to delete remote document", {
					workspaceName,
					documentId,
					error: e,
				});
				throw e;
			}
		},
		getRemote: async (
			token: string,
			workspaceName: string,
			documentID: string
		): Promise<Repo> => {
			const docsapiClient = createDocsApiFetch(token);
			const route = `/repos/${encodeURIComponent(workspaceName)}/${encodeURIComponent(documentID)}`;
			const repo = await docsapiClient(route, { method: "GET" });

			return repo;
		},
		permissions: {
			queryUsersWithAccess: async ({
				token,
				workspaceName,
				documentId,
				permission,
			}: CheckDocPermArgs & {
				permission: DocPermission;
			}): Promise<PermissionInfo> => {
				const docsapiClient = createDocsApiFetch(token);
				const params = new URLSearchParams();
				params.append("permission", permission);
				const resp = await docsapiClient(
					`/permissions/${workspaceName}/${documentId}/info?${params.toString()}`,
					{ method: "GET" }
				);

				return resp;
			},
			queryInvites: async ({
				token,
				workspaceName,
				documentId,
			}: Omit<CheckDocPermArgs, "permission">): Promise<Array<Invitation>> => {
				const docsapiClient = createDocsApiFetch(token);
				const invites = await docsapiClient(
					`/permissions/${workspaceName}/${documentId}/invites`,
					{
						method: "GET",
					}
				);

				const schema = z.array(InviteResultSchema);
				const results = schema.parse(invites);
				return results;
			},
			check: async ({
				token,
				workspaceName,
				documentId,
				permission,
				subject,
			}: CheckDocPermArgs & {
				permission: DocPermission;
				subject?: string;
			}): Promise<boolean> => {
				const docsapiClient = docsApiFetch(token, false);
				const params = new URLSearchParams();
				params.append("permission", permission);
				subject && params.append("subject", subject);

				const url = `${getDocsApiUrl()}/internal/v1/permissions/${workspaceName}/${documentId}?${params.toString()}`;

				const response = await docsapiClient(url, { method: "GET" });

				if (response.ok) {
					return true;
				} else if (response.status == 403 || response.status == 404) {
					return false;
				}

				throw new DocsApiError(response.statusText, response);
			},
			checkMaxTableRead: async ({
				token,
				workspaceName,
				documentId,
				tableIds,
				subject,
			}: CheckDocPermArgs & {
				tableIds: string[];
				subject?: string;
			}): Promise<string> => {
				const docsapiClient = createDocsApiFetch(token);
				const params = new URLSearchParams();
				for (const tableId of tableIds) {
					params.append("tableId", tableId);
				}
				subject && params.append("subject", subject);
				const response = await docsapiClient(
					`/permission/${workspaceName}/${documentId}/storage?${params.toString()}`,
					{ method: "GET" }
				);
				return response;
			},
			checkMaxTableWrite: async ({
				token,
				workspaceName,
				documentId,
				tableId,
				subject,
			}: CheckDocPermArgs & {
				tableId: string;
				subject?: string;
			}): Promise<boolean> => {
				const docsapiClient = docsApiFetch(token);
				const params = new URLSearchParams();
				params.append("permission", "write_rows");
				subject && params.append("subject", subject);
				const response = await docsapiClient(
					`/permission/${workspaceName}/${documentId}/storage/${tableId}?${params.toString()}`,
					{ method: "GET" }
				);

				if (response.ok) {
					return true;
				} else if (response.status == 403 || response.status == 404) {
					return false;
				}

				throw new DocsApiError(response.statusText, response);
			},

			updatePublicAccess: async (
				opts: BaseUpdatePermArgs & { loggedInUsersOnly?: boolean }
			) => {
				const { relation, loggedInUsersOnly, workspaceName, documentId } = opts;
				const body: DocChangePermRequest = {
					type: loggedInUsersOnly ? "LoggedInUsers" : "AllUsers",
					relation,
				};

				await callUpdatePermissions({
					url: `/permissions/${workspaceName}/${documentId}?all=true`,
					body,
					...opts,
				});
			},

			updateWorkspace: async (opts: BaseUpdatePermArgs) => {
				const { relation, workspaceName, documentId } = opts;
				if (workspaceName === LOCAL_WORKSPACE_NAME) {
					throw new Error("Cannot update permissions for local workspace");
				}

				const body: DocChangePermRequest = {
					type: "Workspace",
					relation,
				};
				await callUpdatePermissions({
					url: `/permissions/${workspaceName}/${documentId}`,
					body,
					...opts,
				});
			},

			updateInvite: async (opts: BaseUpdatePermArgs & { email: string }) => {
				const { email, relation, workspaceName, documentId } = opts;
				if (relation === "owner") {
					throw new Error("Cannot invite a user to be an owner");
				}
				const body: DocChangePermRequest = {
					type: "Email",
					relation,
					value: email,
				};

				await callUpdatePermissions({
					url: `/permissions/${workspaceName}/${documentId}`,
					body,
					...opts,
				});
			},

			update: async (opts: BaseUpdatePermArgs & { user: SearchUserResult }) => {
				const { user, relation, workspaceName, documentId } = opts;
				const body: DocChangePermRequest = {
					type: "Username",
					relation,
					value: user.nickname,
				};

				return await callUpdatePermissions({
					url: `/permissions/${workspaceName}/${documentId}`,
					body,
					...opts,
				});
			},
		},
		metadata: {
			read: async (workspaceName: string, documentId: string) => {
				const response = await lookupMetadata(workspaceName, documentId);

				const text = await response.text();
				const metadata = YAML.parse(text);

				return DocumentMetadataSchema.parse(metadata);
			},
		},
		remoteExists: async (
			workspaceName: string,
			documentId: string,
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>
		) => {
			if (!workspaceName) {
				return false;
			}

			const token = await getAccessToken(authenticationActorRef);
			if (!token) {
				throw new Error("Not authenticated");
			}

			let response: Response | undefined;
			try {
				response = await fetch(
					`${getDocsApiUrl()}/internal/v1/${workspaceName}/${documentId}`,
					{
						headers: { Authorization: "Bearer " + token },
					}
				);
			} catch (e) {
				if (e instanceof Error && e.message === "Not found") {
					return false;
				}
				throw e;
			}

			return response.ok;
		},
		// this might have happened when we successfully created a remote repo,
		// but failed to push.
		remoteMetadataExists: async (
			workspaceName: string,
			documentId: string,
			authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>
		) => {
			if (!workspaceName) {
				return false;
			}

			try {
				const response = await lookupMetadata(
					workspaceName,
					documentId,
					authenticationActorRef
				);
				if (!response.ok) {
					return false;
				}
				return true;
			} catch (e) {
				// lookupMetadata should log when there is a real error and not a 404.
				// so going to just return false here.
				return false;
			}
		},

		mergeRequest: {
			list: async (workspaceName: string, documentId: string) => {
				const accessToken = await getAccessToken();

				const gitea = giteaClient(accessToken);

				const response = await gitea.repos.repoListPullRequests(workspaceName, documentId, {
					// Filter out closed merge requests
					state: "open",
				});

				if (!response.ok) {
					console.error("Failed to fetch merge requests", {
						workspaceName,
						documentId,
						response,
					});
					throw new Error("Failed to fetch merge requests");
				}

				return response.data;
			},

			create: async (
				workspaceName: string,
				documentId: string,
				options: CreatePullRequestOption
			) => {
				const accessToken = await getAccessToken();

				const gitea = giteaClient(accessToken);

				const response = await gitea.repos.repoCreatePullRequest(
					workspaceName,
					documentId,
					options
				);

				if (!response.ok) {
					console.error("Failed to create merge request", {
						workspaceName,
						documentId,
						response,
					});
					throw new Error("Failed to create merge request");
				}

				return response.data;
			},

			addReview: async (
				workspaceName: string,
				documentId: string,
				mergeRequestNumber: number,
				review: CreatePullReviewOptions & {
					// PR review event types from https://gitea.com/gitea/go-sdk/src/branch/main/gitea/pull_review.go
					event?:
						| "APPROVED"
						| "PENDING"
						| "COMMENT"
						| "REQUEST_CHANGES"
						| "REQUEST_REVIEW"
						| "";
				}
			) => {
				const accessToken = await getAccessToken();

				const gitea = giteaClient(accessToken);

				const response = await gitea.repos.repoCreatePullReview(
					workspaceName,
					documentId,
					mergeRequestNumber,
					review
				);

				if (!response.ok) {
					console.error("Failed to add review to merge request", {
						workspaceName,
						documentId,
						mergeRequestNumber,
						response,
					});

					throw new Error("Failed to add review to merge request");
				}

				return response.data;
			},

			close: async (
				workspaceName: string,
				documentId: string,
				mergeRequestNumber: number
			) => {
				const accessToken = await getAccessToken();

				const headers = new Headers();
				headers.set("Accept", "application/json");
				headers.set("Content-Type", "application/json");

				if (accessToken) {
					headers.set("Authorization", `Bearer ${accessToken}`);
				}

				const response = await fetch(
					`${getDocsApiUrl()}/api/v1/repos/${workspaceName}/${documentId}/pulls/${mergeRequestNumber}`,
					{
						method: "PATCH",
						headers,
						body: JSON.stringify({
							// https://github.com/go-gitea/gitea/blob/main/modules/structs/issue.go#L19
							state: "closed",
						}),
					}
				);

				if (!response.ok) {
					console.error("Failed to close merge request", {
						workspaceName,
						documentId,
						mergeRequestNumber,
						response,
					});

					throw new Error("Failed to close merge request");
				}

				return response.json() as Promise<PullRequest>;
			},
		},
	},

	workspaces: {
		queryDocuments: async (token: string, workspaceName: string): Promise<Array<Repo>> => {
			const docsapiClient = createDocsApiFetch(token);
			const repos = await docsapiClient(
				`/repos?workspace=${encodeURIComponent(workspaceName)}`,
				{
					method: "GET",
				}
			);

			return repos;
		},
		list: async () => {
			const giteaHost = getDocsApiUrlWithoutProtocol();

			const accessToken = await getAccessToken();
			// cannot list workspaces without an access token
			if (!accessToken) {
				return [];
			}

			const docsapiClient = createDocsApiFetch(accessToken!);
			const {
				owned,
				member,
				guest,
			}: {
				owned: Array<Workspace>;
				member: Array<Workspace>;
				guest: Array<Workspace>;
			} = await docsapiClient(`/workspaces`, { method: "GET" });

			const mappedOwned = new Map(
				owned.map((workspace) => [workspace.display_name, workspace])
			);

			const mappedMember = new Map(
				member
					.filter((w) => !mappedOwned.has(w.display_name))
					.map((workspace) => [workspace.display_name, workspace])
			);

			const mappedGuest = new Map(
				guest
					.filter(
						(w) => !mappedOwned.has(w.display_name) && !mappedMember.has(w.display_name)
					)
					.map((workspace) => [workspace.display_name, workspace])
			);

			const workspaces = [...new Set([...owned, ...guest])];

			const findRole = (workspace: Workspace): "owner" | "member" | "guest" => {
				if (mappedOwned.has(workspace.display_name)) {
					return "owner";
				}
				if (mappedMember.has(workspace.display_name)) {
					return "member";
				}
				return "guest";
			};

			return workspaces.map((workspace) => ({
				id: `${giteaHost}/${workspace.display_name}`,
				name: workspace.display_name,
				host: giteaHost,
				slug: workspace.display_name,
				owner: mappedOwned.has(workspace.display_name),
				role: findRole(workspace),
			}));
		},
	},

	templates: {
		list: async () => {
			const response = await fetch(`${getDocsApiUrl()}/internal/v1/templates`, {
				headers: await buildRawRequestHeaders(),
			});

			if (!response.ok) {
				throw new Error("Failed to fetch templates");
			}

			const templates: Repo[] = await response.json();

			return templates;
		},
	},

	users: {
		query: async (token: string, query: string): Promise<Array<SearchUserResult>> => {
			const docsapiClient = createDocsApiFetch(token);
			const params = new URLSearchParams();
			params.append("q", query);
			const users = await docsapiClient(`/query/users?${params.toString()}`, {
				method: "GET",
			});

			return users;
		},
	},
};
